import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import Layout from '../components/layout'
import Page from '../components/Page'
import { useLocation } from '@reach/router'
import { mutateData } from '../utils'

const PageTemplate = ({ data }) => {
  const siteTitle = data.site.siteMetadata.title
  const allContentfulSortableComponents =
    data.allContentfulSortableComponents.edges
  const sortableFieldItems = data.allSortableComponents.edges
  const page = data.contentfulPage
  const navBarData = data.contentfulNavigationBar
  const footerData = data.contentfulFooterV2
  const siteLanguages = data.site.siteMetadata.languages
  const latestBlogPost = data.latestPost.edges[0].node
  const location = useLocation()
  const sortableComponentData = JSON.parse(
    data.sortableComponentsContent.data.dataString
  )
  const sortableData = mutateData(
    sortableComponentData,
    allContentfulSortableComponents
  )
  const pageAlert = data.contentfulPage.navAlert

  return (
    <Layout
      latestBlogPost={latestBlogPost}
      navBarData={navBarData}
      footerData={footerData}
      siteLanguages={siteLanguages}
      location={location}
      pageAlert={pageAlert}
    >
      <div style={{ background: '#fff' }}>
        <Helmet title={siteTitle} />
        <Page
          data={page}
          sortableData={sortableData}
          sortableFieldItems={sortableFieldItems}
        />
      </div>
    </Layout>
  )
}

export default PageTemplate

export const pageQuery = graphql`
  query ($id: String!, $nodeLocale: String!) {
    site {
      siteMetadata {
        title
        languages {
          defaultLangKey
          langs
        }
      }
    }

    latestPost: allWpPost(
      sort: { fields: date, order: DESC }
      limit: 1
      filter: { locale: { locale: { eq: "en_US" } }, status: { eq: "publish" } }
    ) {
      edges {
        node {
          ...LatestPost
        }
      }
    }

    contentfulNavigationBar(node_locale: { eq: $nodeLocale }) {
      title
      logo {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        description
        gatsbyImageData(layout: CONSTRAINED, width: 30, height: 30)
      }
      superNavLinks {
        label
        order
        defaultUrl
        hide
      }
      links {
        label
        hoverGradient
        inverted
        order
        defaultUrl
        hide
        groupHeader
        groupHeaderUrl
        groupHeaderImage {
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          description
          gatsbyImageData(layout: CONSTRAINED, width: 30, height: 30)
        }
        groupId
        childLinks {
          ctaText
          url
          label
          subtitle
          type
          order
          newProductLink
          hide
          image {
            file {
              fileName
              url
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
            description
          }
        }
      }
    }

    contentfulPage(id: { eq: $id }) {
      title
      slug
      node_locale
      seoImage {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        gatsbyImageData(layout: FULL_WIDTH)
        description
      }
      seoTitle
      canonicalUrl
      description
      ogLocale
      ogType
      ogTitle
      ogDescription
      ogImage {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        gatsbyImageData(layout: FULL_WIDTH)
        description
      }
      hideFromSearch
      blocks {
        page {
          slug
        }
        title
        anchorID
        blockVisibility
        backgroundColor
        imageTextPosition
        subtitle {
          subtitle
          childMarkdownRemark {
            html
          }
        }
        hideTitle
        images {
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          description
          sevenColumnImage: gatsbyImageData(layout: FULL_WIDTH, width: 751)
          iphero: gatsbyImageData(layout: FULL_WIDTH, width: 1900)
          quote: gatsbyImageData(layout: CONSTRAINED, width: 84, height: 84)
          gatsbyImageData(layout: FULL_WIDTH)
        }
        mobileOnlyImages {
          gatsbyImageData(layout: FULL_WIDTH)
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          description
        }
        order
        type
        ctaText
        ctaUrl
        anchorID
        video_ref {
          title
          videoId
          videoPlaylistId
          videoUrl
          backgroundImage {
            file {
              fileName
              url
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: CONSTRAINED, width: 1304, height: 733)
            description
          }
          autoplay
        }
        contactForm_ref {
          portalId
          formId
          loadingText
          webinarKey
          marketoId
          redirect
        }
        spacer_ref {
          backgroundColor
          includeHorizontalRule
          useOusterGradient
          horizontalRuleColor
          horizontalRuleHeight
          height
          mobileHeight
          displayOnMobile
        }
        sortableComponentType
        sortableComponentSortableFields
        sortableComponentNoResultsText
        sortableComponent {
          id
          contentful_id
          pageblock {
            page {
              slug
              title
            }
          }
          order
          title
          description {
            description
            childMarkdownRemark {
              html
            }
          }
          type
          website
          logo {
            file {
              fileName
              url
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
            description
          }
          hqLocation
          regionsServed
          servicesNeeded
          industry
          countriesServed
        }
        components {
          id
          title
          subtitle {
            subtitle
            childMarkdownRemark {
              html
            }
          }
          superHeader
          order
          image {
            description
            file {
              url
              fileName
              details {
                image {
                  height
                  width
                }
              }
            }
            valueIcon: gatsbyImageData(
              layout: CONSTRAINED
              width: 56
              height: 56
            )
            cardsWithIconsIcon: gatsbyImageData(
              layout: CONSTRAINED
              width: 35
              height: 35
            )
            scrollingCardsImage: gatsbyImageData(layout: FULL_WIDTH, width: 304)
            scrollingCardsMobileImage: gatsbyImageData(
              layout: FULL_WIDTH
              width: 268
            )
            fourAcrossSquaresImage: gatsbyImageData(
              layout: FULL_WIDTH
              width: 380
              height: 380
            )
            gatsbyImageData(layout: FULL_WIDTH)
          }
          secondaryImages {
            description
            file {
              url
              fileName
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
          }
          link
          sentenceList
          ctaText
          ctaUrl
          ctaDefaultDisplay
          type
          sensorTypeAndLocation {
            tableData
            id
          }
          focalLength
          fov
          multiCtatext {
            id
            headline
            ctaBodyText {
              ctaBodyText
            }
            ctaUrl
            ctaUrlText
            image {
              description
              file {
                url
                fileName
                details {
                  image {
                    height
                    width
                  }
                }
              }
              gatsbyImageData(layout: FULL_WIDTH)
            }
            ctaUrlInternal
            ctaDefaultDisplay
          }
          backgroundColor
          color
          imageTextPosition
        }
        events {
          id
          title
          subtitle {
            subtitle
            childMarkdownRemark {
              html
            }
          }
          description {
            description
            childMarkdownRemark {
              html
            }
          }
          image {
            description
            file {
              url
              fileName
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
          }
          dateTime
          dateTimeText
          ctaText
          ctaUrl
        }
        isDarkTheme
        backgroundImageFillMode
        alternateDisplayMode
        quoteAuthor
        quoteAuthorTitle
      }
      navAlert {
        linkUrl
        mobileText
        type
        text
        ctaText
        hideAlert
      }
    }

    contentfulFooterV2(node_locale: { eq: $nodeLocale }) {
      logo {
        gatsbyImageData(layout: CONSTRAINED, width: 64)
        file {
          url
          details {
            image {
              height
              width
            }
          }
        }
        description
      }
      logoAttr {
        width
        height
        maxWidth
        maxHeight
      }
      nyseLink
      nyseLogo {
        gatsbyImageData(layout: CONSTRAINED, width: 75)
        file {
          url
        }
        description
      }
      copyrightText
      privacyPolicyText
      privacyPolicyUrl
      siteTermsText
      siteTermsUrl
      basedInText
      sections {
        url
        label
        sublinks {
          label
          url
        }
      }
      mobileButtons {
        label
        url
      }
    }

    allContentfulSortableComponents(
      filter: { node_locale: { eq: $nodeLocale } }
    ) {
      edges {
        node {
          id
          contentful_id
          node_locale
          type
          title
          link
          order
          pageblock {
            page {
              slug
              title
            }
          }
          title
          description {
            description
            childMarkdownRemark {
              html
            }
          }
          type
          website
          logo {
            file {
              fileName
              url
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
            description
          }
        }
      }
    }

    sortableComponentsContent(sys: { id: { eq: "allData" } }) {
      data {
        dataString
      }
    }

    allSortableComponents {
      edges {
        node {
          name
          id
          items {
            type
            validations {
              in
            }
          }
        }
      }
    }
  }
`
